<script setup>
import { computed, onBeforeMount, ref } from 'vue';
import store from '@/lib/store';

/**
 * Support request triage.
 *
 * Every status change here is what produces the response and resolution times the County's
 * monthly report requires. Those timestamps are stamped server-side on transition and cannot
 * be reconstructed afterwards, so the cost of a request sitting untriaged is a permanent hole
 * in the metric rather than a delay.
 */

const requests = ref([]);
const loading = ref(false);
const filters = ref({ type: null, status: null, open: null });
const editing = ref(null);
const editDraft = ref({ status: null, resolution_note: null });

const TYPES = [
    { title: 'Bug', value: 'bug' },
    { title: 'Question', value: 'question' },
    { title: 'Access Request', value: 'access' },
    { title: 'Training Request', value: 'training' },
];

const STATUSES = [
    { title: 'New', value: 'new' },
    { title: 'Acknowledged', value: 'acknowledged' },
    { title: 'In Progress', value: 'in_progress' },
    { title: 'Resolved', value: 'resolved' },
];

const headers = [
    { title: 'Logged', key: 'logged_date' },
    { title: 'Type', key: 'type' },
    { title: 'Status', key: 'status' },
    { title: 'Priority', key: 'priority' },
    { title: 'Description', key: 'description' },
    { title: 'First Response', key: 'first_response_at' },
    { title: 'Resolved', key: 'resolved_at' },
    { title: '', key: 'actions', sortable: false },
];

function labelFor(options, value) {
    return options.find(option => option.value === value)?.title || value;
}

function formatDate(value) {
    return value ? new Date(value).toLocaleString() : '—';
}

const rows = computed(() => requests.value.map(request => ({
    id: request._id,
    logged_date: formatDate(request.logged_date),
    type: labelFor(TYPES, request.type),
    status: labelFor(STATUSES, request.status),
    priority: request.priority,
    description: request.description,
    first_response_at: formatDate(request.first_response_at),
    resolved_at: formatDate(request.resolved_at),
    raw: request,
})));

async function load() {
    loading.value = true;
    try {
        const response = await store.dispatch('getSupportRequestList', filters.value);
        if (response?.success) {
            requests.value = response.data;
        }
    } finally {
        loading.value = false;
    }
}

onBeforeMount(load);

function startEdit(request) {
    editing.value = request;
    editDraft.value = { status: request.status, resolution_note: request.resolution_note };
}

async function saveEdit() {
    const payload = {};
    if (editDraft.value.status !== editing.value.status) {
        payload.status = editDraft.value.status;
    }
    if (editDraft.value.resolution_note !== editing.value.resolution_note) {
        payload.resolution_note = editDraft.value.resolution_note;
    }

    // The API rejects an empty update, so an unchanged form closes rather than sending one.
    if (Object.keys(payload).length === 0) {
        editing.value = null;
        return;
    }

    const response = await store.dispatch('patchSupportRequest', { id: editing.value._id, payload });
    if (response?.success) {
        editing.value = null;
        await load();
    }
}
</script>

<template>
    <v-container fluid>
        <v-row>
            <v-col>
                <h1>Support Requests</h1>
            </v-col>
        </v-row>

        <v-row>
            <v-col cols="12" sm="3">
                <v-select
                    v-model="filters.type"
                    :items="TYPES"
                    label="Type"
                    clearable
                    density="comfortable"
                    @update:modelValue="load"
                />
            </v-col>
            <v-col cols="12" sm="3">
                <v-select
                    v-model="filters.status"
                    :items="STATUSES"
                    label="Status"
                    clearable
                    density="comfortable"
                    @update:modelValue="load"
                />
            </v-col>
            <v-col cols="12" sm="3">
                <v-switch
                    v-model="filters.open"
                    true-value="true"
                    false-value=""
                    label="Open only"
                    color="primary"
                    @update:modelValue="load"
                />
            </v-col>
        </v-row>

        <v-row>
            <v-col>
                <v-data-table
                    :headers="headers"
                    :items="rows"
                    :loading="loading"
                    :items-per-page="50"
                >
                    <!-- Dynamic slot name, not `#item.actions`: eslint's vue/valid-v-slot
                         reads the dot as a modifier and fails the build. Repo convention. -->
                    <template v-slot:[`item.actions`]="{ item }">
                        <v-btn size="small" variant="text" @click="startEdit(item.raw)">Update</v-btn>
                    </template>
                    <template #no-data>
                        <div class="py-4 text-medium-emphasis">No support requests match these filters.</div>
                    </template>
                </v-data-table>
            </v-col>
        </v-row>

        <v-dialog :model-value="!!editing" width="auto" @update:modelValue="editing = null">
            <v-card max-width="600" title="Update support request">
                <v-container>
                    <v-form>
                        <v-row>
                            <v-col>
                                <v-select
                                    v-model="editDraft.status"
                                    :items="STATUSES"
                                    label="Status"
                                    variant="filled"
                                />
                            </v-col>
                        </v-row>
                        <v-row>
                            <v-col>
                                <v-textarea
                                    v-model="editDraft.resolution_note"
                                    label="Resolution Note"
                                    variant="filled"
                                    auto-grow
                                />
                            </v-col>
                        </v-row>
                    </v-form>
                </v-container>
                <template #actions>
                    <v-btn @click="saveEdit">Save</v-btn>
                    <v-btn text="Cancel" @click="editing = null" />
                </template>
            </v-card>
        </v-dialog>
    </v-container>
</template>
